import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Minus, Plus, CheckCircle2, XCircle, RotateCcw, Leaf } from "lucide-react";
import { api, apiError } from "../lib/api";
import { DisclaimerBanner } from "../components/Layout";
import { toast } from "sonner";

export default function ChapterReader() {
  const { number } = useParams();
  const chapterNumber = parseInt(number, 10);
  const navigate = useNavigate();

  const [chapter, setChapter] = useState(null);
  const [error, setError] = useState(null);
  const [fontScale, setFontScale] = useState(1.125); // rem
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [total, setTotal] = useState(90);

  const load = useCallback(async () => {
    setError(null);
    setResult(null);
    setAnswers({});
    try {
      const [ch, ov] = await Promise.all([
        api.get(`/course/chapters/${chapterNumber}`),
        api.get("/course/overview"),
      ]);
      setChapter(ch.data);
      setTotal(ov.data.total_chapters);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(apiError(e));
    }
  }, [chapterNumber]);

  useEffect(() => { load(); }, [load]);

  const submitQuiz = async () => {
    if (Object.keys(answers).length < (chapter?.quiz?.length || 0)) {
      toast.error("Please answer every question before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/course/chapters/${chapterNumber}/quiz`, { answers });
      setResult(data);
      if (data.passed) toast.success(`Passed with ${data.score}%! Chapter complete.`);
      else toast.error(`Scored ${data.score}%. You need ${data.pass_threshold}% to pass — try again.`);
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => { setResult(null); setAnswers({}); window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <p className="text-[#C47A53] font-medium">{error}</p>
        <button onClick={() => navigate("/dashboard")} className="mt-4 px-5 py-2.5 rounded-full bg-[#2C4A3E] text-[#FAF8F5]">Back to dashboard</button>
      </div>
    );
  }
  if (!chapter) return <div className="max-w-3xl mx-auto px-4 py-16 text-[#5A6B61]">Loading chapter…</div>;

  const resById = (id) => result?.results?.find((r) => r.id === id);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard" className="flex items-center gap-2 text-sm text-[#2C4A3E] mb-6">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </button>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <span className="font-mono text-xs uppercase tracking-widest text-[#829B88]">{chapter.module_title}</span>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl text-[#1A2621]">
            <span className="text-[#829B88]">Ch. {chapter.chapter_number}.</span> {chapter.title}
          </h1>
        </div>
        <div className="flex items-center gap-1 bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-full p-1">
          <button onClick={() => setFontScale((s) => Math.max(0.95, s - 0.0625))} data-testid="font-decrease" className="w-8 h-8 rounded-full hover:bg-[#E8ECE8] flex items-center justify-center text-[#2C4A3E]"><Minus className="w-4 h-4" /></button>
          <span className="text-xs font-mono text-[#5A6B61] px-1">Aa</span>
          <button onClick={() => setFontScale((s) => Math.min(1.5, s + 0.0625))} data-testid="font-increase" className="w-8 h-8 rounded-full hover:bg-[#E8ECE8] flex items-center justify-center text-[#2C4A3E]"><Plus className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-4 text-sm text-[#5A6B61]">
        <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> ~{chapter.reading_minutes} min read</span>
        {chapter.is_completed && <span className="flex items-center gap-1.5 text-[#3D7A5A]"><CheckCircle2 className="w-4 h-4" /> Completed</span>}
      </div>

      <DisclaimerBanner className="mt-5" />

      <article
        className="reading-content mt-8 text-[#2C3831] leading-loose font-light"
        style={{ fontSize: `${fontScale}rem` }}
      >
        {chapter.content}
      </article>

      {/* Quiz */}
      <div className="mt-12 pt-8 border-t border-[#2C4A3E]/12">
        <div className="flex items-center gap-2 mb-1">
          <Leaf className="w-5 h-5 text-[#829B88]" />
          <h2 className="font-serif text-2xl text-[#1A2621]">Chapter Quiz</h2>
        </div>
        <p className="text-sm text-[#5A6B61] mb-6">Answer all {chapter.quiz.length} questions. You need 85% to pass and complete this chapter.</p>

        <div className="space-y-6">
          {chapter.quiz.map((q, qi) => {
            const r = resById(q.id);
            return (
              <div key={q.id} data-testid={`quiz-q-${qi}`} className="bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl p-5">
                <p className="font-medium text-[#1A2621]">{qi + 1}. {q.question}</p>
                <div className="mt-4 space-y-2">
                  {q.options.map((opt, oi) => {
                    const selected = answers[q.id] === oi;
                    let stateClass = "bg-white/60 border-[#2C4A3E]/15 hover:bg-[#829B88]/10";
                    if (result) {
                      if (oi === r?.correct_index) stateClass = "bg-[#3D7A5A]/12 border-[#3D7A5A]";
                      else if (selected && !r?.is_correct) stateClass = "bg-[#C47A53]/12 border-[#C47A53]";
                      else stateClass = "bg-white/40 border-[#2C4A3E]/10";
                    } else if (selected) {
                      stateClass = "bg-[#829B88]/15 border-[#2C4A3E]/50";
                    }
                    return (
                      <button
                        key={oi}
                        disabled={!!result}
                        data-testid={`quiz-q-${qi}-opt-${oi}`}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                        className={`w-full text-left p-4 rounded-xl border flex items-center gap-3 transition-all ${stateClass}`}
                      >
                        <span className={`w-6 h-6 shrink-0 rounded-full border flex items-center justify-center text-xs font-mono ${selected && !result ? "bg-[#2C4A3E] text-[#FAF8F5] border-[#2C4A3E]" : "border-[#2C4A3E]/30 text-[#5A6B61]"}`}>
                          {String.fromCharCode(65 + oi)}
                        </span>
                        <span className="text-sm text-[#334139]">{opt}</span>
                        {result && oi === r?.correct_index && <CheckCircle2 className="w-4 h-4 text-[#3D7A5A] ml-auto" />}
                        {result && selected && !r?.is_correct && <XCircle className="w-4 h-4 text-[#C47A53] ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {result ? (
          <div className="mt-6">
            <div className={`rounded-2xl p-6 text-center ${result.passed ? "bg-[#3D7A5A]/10 border border-[#3D7A5A]/30" : "bg-[#C47A53]/10 border border-[#C47A53]/30"}`}>
              <p className="font-serif text-3xl text-[#1A2621]">{result.score}%</p>
              <p className={`mt-1 font-medium ${result.passed ? "text-[#3D7A5A]" : "text-[#C47A53]"}`}>
                {result.passed ? "Passed — chapter complete!" : `Not quite — you need ${result.pass_threshold}%.`}
              </p>
              <p className="text-sm text-[#5A6B61] mt-1">{result.correct} of {result.total} correct</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-3 justify-center">
              {!result.passed && (
                <button onClick={retry} data-testid="retry-quiz-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Retry quiz</button>
              )}
              {result.passed && chapterNumber < total && (
                <button onClick={() => navigate(`/chapter/${chapterNumber + 1}`)} data-testid="next-chapter-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold flex items-center gap-2">Next chapter <ArrowRight className="w-4 h-4" /></button>
              )}
              {result.passed && chapterNumber >= total && result.progress?.final_exam_unlocked && (
                <button onClick={() => navigate("/final-exam")} data-testid="go-final-exam-btn" className="px-6 py-3 rounded-full bg-[#C69255] text-[#1A2621] font-semibold">Take the Final Exam</button>
              )}
              <button onClick={() => navigate("/dashboard")} className="px-6 py-3 rounded-full border border-[#2C4A3E]/25 text-[#2C4A3E] font-semibold">Dashboard</button>
            </div>
          </div>
        ) : (
          <button onClick={submitQuiz} disabled={submitting} data-testid="quiz-submit-btn" className="mt-6 w-full py-4 rounded-xl bg-[#2C4A3E] text-[#FAF8F5] font-semibold hover:bg-[#223B31] transition-colors disabled:opacity-60">
            {submitting ? "Grading…" : "Submit quiz"}
          </button>
        )}
      </div>
    </div>
  );
}
